// src/modules/app-views/invoices/pages/Document/tabs/AttachmentsTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Card, Empty, Typography } from 'antd';
import {
  FilePdfOutlined,
  FileTextOutlined,
  FileImageOutlined,
  FileOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import translateX from 'utils/translateX';
import { isLockedStatus } from 'services/fiscalService';
import dayjs from 'dayjs';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MIME_TYPE_ICONS = {
  'application/pdf': { icon: FilePdfOutlined, color: '#ff4d4f' },
  'text/xml': { icon: FileTextOutlined, color: '#52c41a' },
  'application/xml': { icon: FileTextOutlined, color: '#52c41a' },
  'image/png': { icon: FileImageOutlined, color: '#1890ff' },
  'image/jpeg': { icon: FileImageOutlined, color: '#1890ff' },
  'image/jpg': { icon: FileImageOutlined, color: '#1890ff' },
  default: { icon: FileOutlined, color: '#8c8c8c' },
};

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Format file size for display
 */

/**
 * Download file from URL (fetches as blob to force download instead of opening in new tab)
 */
const downloadFromUrl = async (url, fileName) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response was not ok');
    
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    console.error('[AttachmentsTab] Failed to download from URL:', err);
    // Fallback to opening in new tab
    window.open(url, '_blank');
  }
};

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get file extension from filename or mimeType
 */
function getFileExtension(filename, mimeType) {
  // Try to get from filename
  if (filename) {
    const match = filename.match(/\.([a-zA-Z0-9]+)$/);
    if (match) return match[1].toUpperCase();
  }
  // Fallback to mimeType
  if (mimeType) {
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('xml')) return 'XML';
    if (mimeType.includes('png')) return 'PNG';
    if (mimeType.includes('jpeg') || mimeType.includes('jpg')) return 'JPG';
  }
  return '';
}

/**
 * Get icon config for mime type
 */
function getIconConfig(mimeType) {
  return MIME_TYPE_ICONS[mimeType] || MIME_TYPE_ICONS.default;
}

/**
 * Build file list from invoice attachments
 */
function buildFileList(data) {
  const files = [];
  const attachments = data?.attachments || [];

  attachments.forEach((attachment, index) => {
    files.push({
      uid: attachment._id?.$oid || attachment.attachmentId?.$oid || `attachment-${index}`,
      name: attachment.originalName || attachment.label || `Anexo ${index + 1}`,
      url: attachment.storageUrl,
      mimeType: attachment.mimeType,
      label: attachment.label,
      tags: attachment.tags || [],
      sortOrder: attachment.sortOrder || index,
      isPrimary: attachment.isPrimary,
    });
  });

  // Sort by sortOrder
  files.sort((a, b) => a.sortOrder - b.sortOrder);

  return files;
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AttachmentsTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const isNew = !data?._id || data?._id === 'add';
  const isLocked = isLockedStatus(data?.status);

  // Build file list from invoice attachments
  const fileList = useMemo(() => buildFileList(data), [data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOpenFile = file => {
    if (file.url) {
      // Force download instead of opening in browser
      downloadFromUrl(file.url, file.name);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (isNew) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={translateX('save_document_to_see_attachments')}
        />
      </Card>
    );
  }

  if (fileList.length === 0) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={translateX('no_attachments_available')}
        />
      </Card>
    );
  }

  return (
    <Card title={translateX('attachments')} className="attachments-tab-card">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {fileList.map(file => {
          const iconConfig = getIconConfig(file.mimeType);
          const IconComponent = iconConfig.icon;
          const extension = getFileExtension(file.name, file.mimeType);

          return (
            <div
              key={file.uid}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: '#fafafa',
              }}
              onClick={() => handleOpenFile(file)}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#1890ff';
                e.currentTarget.style.backgroundColor = '#f0f7ff';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#d9d9d9';
                e.currentTarget.style.backgroundColor = '#fafafa';
              }}
            >
              {/* Icon */}
              <div style={{ marginRight: 16 }}>
                <IconComponent style={{ fontSize: 32, color: iconConfig.color }} />
              </div>

              {/* File info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 14,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginBottom: 4,
                  }}
                >
                  {file.name}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {extension && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {extension}
                    </Text>
                  )}
                  {file.label && file.label !== file.name && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {file.label}
                    </Text>
                  )}
                  {file.tags?.length > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {file.tags.join(', ')}
                    </Text>
                  )}
                </div>
              </div>

              {/* Delete button - disabled for locked status */}
              <div
                style={{ marginLeft: 16, padding: 8 }}
                onClick={e => {
                  e.stopPropagation();
                  // Disabled for fiscal documents
                }}
              >
                <DeleteOutlined
                  style={{
                    fontSize: 16,
                    color: isLocked ? '#d9d9d9' : '#d9d9d9',
                    cursor: 'not-allowed',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AttachmentsTab;
